import React, { useContext, useState, useEffect } from "react";
import { classes } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { StoryListItem } from "@bloomberg/media-ui-article";
import { ScreenContext } from "@foundation/screen";
import { useFortress } from "@foundation/fortress";
import { Refresh } from "@bloomberg/media-ui-icons";
import styles from "./watchlistNews.module.css";
import { WatchlistNewsPlaceholder } from "./watchlistNewsPlaceholder";
import type { PersonalizationSecurityNews } from "~types/types";
import { getFortressState } from "~utils/getFortressState";
import { fetchWatchlistNewsData } from "~api/portfolio/fetchWatchlistNewsData";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import {
  LOADING_STATE,
  WATCHLIST_NEWS_FAILURE_TEXT,
} from "~constants/Portfolio";
import Notification from "~components/Notification";

const CURRENT_TAG_TYPE = "Company";
const MAX_NEWS_FEED = 10;

function trackStoryListItemClick() {
  trackClickEvent(
    "WatchlistNewsStory",
    "Watchlist News Story Click",
    "click",
    "watchlist"
  );
}

export default function WatchlistNews() {
  const {
    breakpoints: { tablet },
  } = useContext(ScreenContext);
  const fortressContext = useFortress();
  const { isAllowed } = getFortressState(fortressContext);
  const [stories, setStories] = useState<Array<PersonalizationSecurityNews>>(
    []
  );
  const [loadingState, setLoadingState] = useState(LOADING_STATE.EMPTY);

  const { selectedPortfolio } = usePortfolioPage();

  const refreshWatchlistNews = () => {
    if (!selectedPortfolio?.positions?.length) {
      setStories([]);

      return;
    }

    setLoadingState(LOADING_STATE.LOADING);
    const formattedTickers =
      selectedPortfolio?.positions?.map(
        (position) => `${CURRENT_TAG_TYPE}|${position.security.ticker}`
      ) || [];

    const currentTags = formattedTickers.join(";");

    fetchWatchlistNewsData(currentTags)
      .then((data: PersonalizationSecurityNews[]) => {
        setStories(tablet === false ? data.slice(0, MAX_NEWS_FEED) : data);
        setLoadingState(LOADING_STATE.COMPLETE);
      })
      .catch((e) => {
        console.error(e);
        // will handle in Notification once separate PR is merged
        setLoadingState(LOADING_STATE.FAILED);
      });
  };

  useEffect(() => {
    refreshWatchlistNews();
  }, [tablet, selectedPortfolio?.portInfo.id]);

  if (loadingState === LOADING_STATE.LOADING) {
    return <WatchlistNewsPlaceholder />;
  } else if (selectedPortfolio && loadingState === LOADING_STATE.FAILED) {
    return (
      <Notification
        text={WATCHLIST_NEWS_FAILURE_TEXT}
        variant="default"
        title=""
        actionIcon={<Refresh />}
        actionText="Refresh"
        onActionClick={refreshWatchlistNews}
      />
    );
  }

  if (!stories?.length) {
    return null;
  }

  return (
    <div className={classes([styles.watchlistNews, !isAllowed])}>
      <div className={styles.title}>Watchlist News</div>
      <div className={styles.news}>
        {stories.map((story) => {
          const thumbnail = {
            src: story.thumbnailUrl,
          };

          return (
            <StoryListItem
              onClick={trackStoryListItemClick}
              className={styles.storyItem}
              key={story.assetID}
              displayImage
              headline={story.headline}
              thumbnail={thumbnail}
              href={story.url}
              type={story.assetType}
              duration={story.duration}
              displayTimestamp
              publishedAt={story.publishedAt}
              showTimestampDateAfterCutoff={true}
              timestampCutoffHours={24}
            />
          );
        })}
      </div>
    </div>
  );
}
